/**
 * Prometheus 텍스트 파서 — 대시보드가 쓰는 만큼만.
 *
 * 컨트롤러는 이미 `/metrics` 로 운영에 필요한 수치를 다 내놓고 있어서, 대시보드용 API 를
 * 따로 만들지 않았다. 노출면이 안 늘고, 콘솔이 보는 숫자와 알림이 보는 숫자가 같아진다.
 *
 * **여기 없는 것은 시계열이다.** 우리는 지표를 저장하지 않으므로(TSDB 없음) "지난 6시간
 * RPS 그래프" 같은 건 만들 수 없다 — 대신 대시보드는 `createdAt` 처럼 **지금 조회로
 * 알아낼 수 있는 사실**로 추이를 그린다. 없는 히스토리를 그럴듯하게 그리면 그건 데이터가
 * 아니라 장식이다.
 */

export interface Metric {
  name: string;
  labels: Record<string, string>;
  value: number;
}

const LABEL_RE = /([a-zA-Z_][a-zA-Z0-9_]*)="([^"]*)"/g;

export function parseMetrics(text: string): Metric[] {
  const out: Metric[] = [];
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line || line.startsWith("#")) continue;
    const sp = line.lastIndexOf(" ");
    if (sp < 0) continue;
    const value = Number(line.slice(sp + 1));
    if (!Number.isFinite(value)) continue;
    const head = line.slice(0, sp);
    const brace = head.indexOf("{");
    if (brace < 0) {
      out.push({ name: head, labels: {}, value });
      continue;
    }
    const labels: Record<string, string> = {};
    for (const m of head.slice(brace).matchAll(LABEL_RE)) labels[m[1]!] = m[2]!;
    out.push({ name: head.slice(0, brace), labels, value });
  }
  return out;
}

/** 이름(+라벨 일부)이 맞는 것들의 합. 없으면 0 — 카운터는 안 나타난 게 곧 0 이다. */
export function sum(
  metrics: Metric[],
  name: string,
  match: (labels: Record<string, string>) => boolean = () => true,
): number {
  let total = 0;
  for (const m of metrics) if (m.name === name && match(m.labels)) total += m.value;
  return total;
}

export function one(metrics: Metric[], name: string): number {
  return sum(metrics, name);
}

/** 라벨 값별로 접어서 큰 것부터. 상태·사유 칩을 만드는 데 쓴다. */
export function byLabel(
  metrics: Metric[],
  name: string,
  label: string,
): Array<{ key: string; value: number }> {
  const acc = new Map<string, number>();
  for (const m of metrics) {
    if (m.name !== name) continue;
    const key = m.labels[label] ?? "?";
    acc.set(key, (acc.get(key) ?? 0) + m.value);
  }
  return [...acc].map(([key, value]) => ({ key, value })).sort((a, b) => b.value - a.value);
}
